/**
 * Stripe Elements `appearance` derived from the landlord design tokens, so the embedded
 * address / card fields follow light and dark mode. Colors are read from the live CSS variables
 * (styles/tokens.css) — never hardcoded here — as `hsl(...)` strings, which Stripe accepts.
 */

import type { Appearance } from '@stripe/stripe-js';
import type { ResolvedTheme } from './theme';

/** Stripe appearance variable -> the design token it mirrors. */
const VARIABLE_TOKENS = {
  colorBackground: '--background',
  colorText: '--foreground',
  colorTextSecondary: '--muted-foreground',
  colorTextPlaceholder: '--muted-foreground',
  colorPrimary: '--primary',
  colorDanger: '--destructive',
} as const;

function readToken(style: CSSStyleDeclaration, token: string): string | null {
  const value = style.getPropertyValue(token).trim();
  return value ? `hsl(${value})` : null;
}

export function buildStripeAppearance(
  resolved: ResolvedTheme,
  root: HTMLElement = document.documentElement,
): Appearance {
  const style = getComputedStyle(root);
  const variables: Record<string, string> = { borderRadius: '8px' };
  for (const [variable, token] of Object.entries(VARIABLE_TOKENS)) {
    const color = readToken(style, token);
    if (color) variables[variable] = color;
  }
  const border = readToken(style, '--border');
  return {
    // `night` is Stripe's dark base; the token variables above override its palette.
    theme: resolved === 'dark' ? 'night' : 'stripe',
    variables,
    rules: border ? { '.Input': { border: `1px solid ${border}` } } : {},
  };
}
